/**
 * Runtime Options Form Component
 *
 * Reusable form for configuring agent runtime options.
 * Used in workflow execution dialogs and scheduled task forms.
 *
 * These options control agent behavior during workflow execution
 * to prevent affecting user's real memory.
 */

import React from 'react';
import type { RuntimeOptions } from '@/types/imageWorkflow';
import { useI18n } from '@/i18n';

interface RuntimeOptionsFormProps {
  value: RuntimeOptions;
  onChange: (value: RuntimeOptions) => void;
  /** Show as collapsible section with border */
  showBorder?: boolean;
}

export const RuntimeOptionsForm: React.FC<RuntimeOptionsFormProps> = ({
  value,
  onChange,
  showBorder = true,
}) => {
  const { t } = useI18n();
  const handleChange = (key: keyof RuntimeOptions, checked: boolean) => {
    onChange({
      ...value,
      [key]: checked,
    });
  };

  return (
    <div className={`space-y-3 ${showBorder ? 'border-t pt-4' : ''}`}>
      <label className="block text-sm font-medium text-gray-500">
        {t('imageWorkflows.runtimeOptions.title')}
      </label>

      {/* Disable Memory Tool */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={value.disable_memory_tool ?? true}
          onChange={(e) => handleChange('disable_memory_tool', e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <div>
          <div className="text-sm text-gray-700">{t('imageWorkflows.runtimeOptions.disableMemoryTool.label')}</div>
          <div className="text-xs text-gray-500">
            {t('imageWorkflows.runtimeOptions.disableMemoryTool.description')}
          </div>
        </div>
      </label>

      {/* Disable Memory Write */}
      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={value.disable_memory_write ?? true}
          onChange={(e) => handleChange('disable_memory_write', e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <div>
          <div className="text-sm text-gray-700">{t('imageWorkflows.runtimeOptions.disableMemoryWrite.label')}</div>
          <div className="text-xs text-gray-500">
            {t('imageWorkflows.runtimeOptions.disableMemoryWrite.description')}
          </div>
        </div>
      </label>
    </div>
  );
};

export default RuntimeOptionsForm;
